import React, {forwardRef} from 'react';

import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './field-description.component.module.styl';

import type {ComponentPropsWithoutRef, ReactNode, Ref} from 'react';


/**
 * Color/severity variants for `FieldDescription`.
 *
 * - `Types.DEFAULT` — neutral helper text, no special styling.
 * - `Types.ERROR` — red error text; also sets `role="alert"` on the element so
 *   screen readers announce the message immediately.
 */
export const Types = {
    DEFAULT: 'Types.DEFAULT',
    ERROR: 'Types.ERROR',
} as const;
const TYPES_CLASSES = {
    [Types.DEFAULT]: null,
    [Types.ERROR]: 'field-description_error',
};
const classNames = withCssModule(styles);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TypesValues = [...Object.values(Types)] as const;
type Type = typeof TypesValues[number];

type NativeProps = ComponentPropsWithoutRef<'div'>;
interface Props extends NativeProps {
    /**
     * Helper text displayed as the description. Prefer `children` instead.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use children
     */
    caption?: ReactNode,

    /**
     * When `false`, the element is hidden via CSS. Prefer conditional rendering
     * over this prop — unmount the component rather than hiding it.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. do not render if not shown
     */
    shown?: boolean,

    /**
     * Styling variant. Use `color` instead — this prop is kept for backwards
     * compatibility only and has identical semantics.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use color
     */
    type?: Type,

    /**
     * Styling variant controlling color and ARIA role:
     * - `Types.DEFAULT` — neutral helper text (no ARIA role).
     * - `Types.ERROR` — red error text with `role="alert"` for immediate
     *   screen-reader announcement. Override the role via the native `role` prop.
     */
    color?: Type,

    /**
     * Forwarded ref to the underlying `<div>` element.
     */
    ref?: Ref<HTMLDivElement>,
}

/**
 * Helper text or validation error displayed below a form field.
 *
 * Renders a `<div>` with default or error styling controlled by the `color`
 * prop (`Types.DEFAULT` | `Types.ERROR`). When `color` is `Types.ERROR` the
 * element automatically receives `role="alert"` so screen readers announce the
 * validation message immediately; override with an explicit `role` prop when
 * needed.
 *
 * ## Accessibility
 * - `color="Types.ERROR"` → `role="alert"` (assertive announcement)
 * - All other colors → no ARIA role (use `role` prop to add one if needed)
 * - Pair with an `aria-describedby` on the related form control pointing at
 *   this element's `id` for full screen-reader association.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-field-description--documentation}
 */
export const FieldDescription = forwardRef<HTMLDivElement, Props>(function FieldDescription(props, ref) {
    const {color, type, shown, caption, ...nativeProps} = props;

    const fieldDescriptionClasses = classNames(
        'field-description', nativeProps.className,
        {
            'field-description_shown': shown ?? true,
        },
        TYPES_CLASSES[type ?? Types.DEFAULT],
        TYPES_CLASSES[color ?? Types.DEFAULT],
    );

    const defaultRole = (type === Types.ERROR || color === Types.ERROR) ? 'alert' : undefined;

    return (
        <div
            {...nativeProps}
            role={nativeProps?.role ?? defaultRole}
            className={fieldDescriptionClasses}
            ref={ref}>
            {caption}{nativeProps.children}
        </div>
    );
});

FieldDescription.displayName = 'FieldDescription';
